import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Network, BookOpen, Link2 } from 'lucide-react';
import { hadithApi } from '../services/api';
import { PageLoading, GlassCard } from '../components/shared';
import { SanadTree } from '../components/SanadTree';
import { RelatedContent } from '../components/RelatedContent';
import FontSizeControl from '../components/FontSizeControl';

const HadithDetail = () => {
  const { t } = useTranslation();
  const { entryId } = useParams();
  const [data, setData] = useState(null);
  const [sanad, setSanad] = useState(null);
  const [loadingSanad, setLoadingSanad] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    hadithApi.getEntry(entryId)
      .then(res => {
        setData(res.data);
        if (res.data.entry.has_sanad_data) {
          setLoadingSanad(true);
          hadithApi.getSanad(entryId)
            .then(s => setSanad(s.data))
            .catch(console.error)
            .finally(() => setLoadingSanad(false));
        } else {
          setSanad(null);
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0 });
  }, [entryId]);

  if (loading) return <PageLoading />;
  if (!data) return null;

  const { entry, collection, book } = data;

  return (
    <div className="min-h-screen py-8 px-4" data-testid="hadith-detail-page">
      <div className="max-w-4xl mx-auto">
        <Link
          to={`/hadith/${entry.collection_slug}/book/${entry.book_number}`}
          className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-primary mb-6"
          data-testid="hadith-back-to-entries"
        >
          <ChevronLeft className="w-4 h-4" />
          {book?.name_en}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
        >
          <div className="mb-8">
            <div className="flex items-center gap-2 text-xs text-text-muted mb-2">
              <BookOpen className="w-3.5 h-3.5" />
              <Link to={`/hadith/${entry.collection_slug}`} className="hover:text-primary">
                {collection?.name_fr || collection?.name_en}
              </Link>
              <span>›</span>
              <span>{t('hadith.book_label', { number: entry.book_number })}</span>
              <span>›</span>
              <span className="text-primary">{t('hadith.hadith_number', { n: entry.id_in_book })}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-serif text-text-primary">
              {collection?.name_fr || collection?.name_en} · {t('hadith.hadith_n', { n: entry.id_in_book })}
            </h1>
            {entry.narrator_english && (
              <p className="text-sm italic text-text-secondary mt-3">{entry.narrator_english}</p>
            )}
          </div>

          <GlassCard className="mb-6" data-testid="hadith-detail-arabic">
            <div className="flex items-center justify-between mb-3">
              <span className="overline">{t('hadith.arabic_text')}</span>
              <FontSizeControl
                prefKey="hadithFontSize"
                testid="hadith-font-size-control"
              />
            </div>
            <p className="hadith-arabic-text text-text-primary" dir="rtl">
              {entry.text_arabic}
            </p>
          </GlassCard>

          {entry.text_english && (
            <GlassCard className="mb-6" data-testid="hadith-detail-english">
              <span className="overline mb-3 block">{t('hadith.english_translation')}</span>
              <p className="text-base text-text-secondary leading-relaxed whitespace-pre-line">
                {entry.text_english}
              </p>
            </GlassCard>
          )}

          <div className="mt-8">
            <div className="flex items-center gap-2 mb-4">
              <Network className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-serif text-text-primary">{t('hadith.isnad_title')}</h2>
            </div>
            {loadingSanad && (
              <p className="text-sm text-text-muted">{t('hadith.loading_chains')}</p>
            )}
            {!loadingSanad && sanad && sanad.chains.length > 0 && (
              <SanadTree chains={sanad.chains} narrators={sanad.narrators} />
            )}
            {!loadingSanad && (!sanad || sanad.chains.length === 0) && (
              <div className="glass-card p-5 text-sm text-text-muted">
                {t('hadith.no_sanad')}
              </div>
            )}
          </div>

          <div className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <Link2 className="w-5 h-5 text-primary" />
              <h2 className="text-xl font-serif text-text-primary">{t('hadith.related_content')}</h2>
            </div>
            <RelatedContent
              entityType="hadith"
              entityId={entry.id}
              title={null}
              types={['ayah', 'tafsir_group', 'event', 'prophet']}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HadithDetail;
